// Backend API surface for the per-actor-scene flow (god-reference 2026-06-25).
// Mirrors server/routes/scenes.py.

import { API_BASE } from "./client";

export interface ActorScene {
  n: number;
  label: string;          // "SC 1"
  name: string;           // "Amy's Apartment"
  slug: string;           // "INT. AMY'S CITY APARTMENT"
  parser_scene_ids: number[];
  line_ids: number[];
  characters: string[];
  line_count: number;
  est_minutes: number;
  status?: "ready" | "recorded" | "matched";
}

export interface ActorScenesEnvelope {
  project_id: string;
  title: string | null;
  characters: string[];
  actor_scenes: ActorScene[];
  line_chars: Record<number, string | null>;   // line_id → character
  line_texts: Record<number, string>;          // line_id → text
}

export interface SceneManifestEntry {
  line_id: number;
  start_ms: number;
  end_ms: number;
  matched?: number;
  expected?: number;
}

export interface SceneTake {
  project_id: string;
  scene_n: number;
  scene_label: string;
  scene_name: string;
  audio_url: string;            // absolute URL after _absolutize
  audio_bytes: number;
  audio_mime: string;
  audio_ext: string;
  duration_ms: number | null;
  created_at: string;
  match_status: "not_started" | "starting" | "asr_done" | "aligning" | "complete" | "error";
  match_progress?: number;
}

export interface MatchStatus {
  project_id?: string;
  scene_n?: number;
  status: "not_started" | "starting" | "asr_done" | "aligning" | "complete" | "error";
  progress: number;             // 0-5
  manifest?: SceneManifestEntry[];
  cue_count?: number;
  matched_cues?: number;
  error?: string;
  updated_at?: string;
}

function abs(url: string): string {
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}

export async function getActorScenes(projectId: string): Promise<ActorScenesEnvelope> {
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/actor-scenes`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface SkipSpan { start_ms: number; end_ms: number }

export async function postSceneTake(
  projectId: string, sceneN: number, audioBlob: Blob,
  opts?: { durationMs?: number; skipSpans?: SkipSpan[] },
): Promise<SceneTake> {
  const fd = new FormData();
  const ext = audioBlob.type.includes("mp4") ? "mp4"
            : audioBlob.type.includes("webm") ? "webm"
            : audioBlob.type.includes("m4a") ? "m4a"
            : "webm";
  fd.append("audio", new File([audioBlob], `scene-${sceneN}.${ext}`, { type: audioBlob.type }));
  if (opts?.durationMs !== undefined) fd.append("duration_ms", String(Math.floor(opts.durationMs)));
  if (opts?.skipSpans && opts.skipSpans.length) fd.append("skip_spans", JSON.stringify(opts.skipSpans));
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/scenes/${sceneN}/take`,
    { method: "POST", body: fd },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const env = await res.json();
  env.audio_url = abs(env.audio_url);
  return env;
}

export async function getSceneTake(projectId: string, sceneN: number): Promise<SceneTake | null> {
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/scenes/${sceneN}/take`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const env = await res.json();
  env.audio_url = abs(env.audio_url);
  return env;
}

export async function startMatch(projectId: string, sceneN: number): Promise<MatchStatus> {
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/scenes/${sceneN}/match`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function getMatch(projectId: string, sceneN: number): Promise<MatchStatus> {
  const res = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/scenes/${sceneN}/match`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface LineRetakeResult {
  project_id: string;
  scene_n: number;
  line_id: number;
  old_dur_ms: number;
  new_dur_ms: number;
  delta_ms: number;
  manifest: SceneManifestEntry[];
}

export async function postLineRetake(
  projectId: string, sceneN: number, lineId: number, audioBlob: Blob,
): Promise<LineRetakeResult> {
  const fd = new FormData();
  const ext = audioBlob.type.includes("mp4") ? "mp4"
            : audioBlob.type.includes("webm") ? "webm"
            : audioBlob.type.includes("m4a") ? "m4a"
            : "webm";
  fd.append("audio", new File([audioBlob], `line-${lineId}.${ext}`, { type: audioBlob.type }));
  const res = await fetch(
    `${API_BASE}/projects/${encodeURIComponent(projectId)}/scenes/${sceneN}/lines/${lineId}/take`,
    { method: "POST", body: fd },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}
